import { Component, OnInit, ViewChild, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { ContactSearchComponent } from '../../common/contact-search/contact-search.component';

import { TinymceComponent } from '../../common/tinymce/tinymce.component';
import { TinymceCssComponent } from '../../common/tinymce-css/tinymce-css.component';

import { DocPart } from '../../../models/doc-part';
import { DocTemplate } from '../../../models/doc-template';

import { DocPartService } from '../../../services/documents/doc-part.service';
import { DocTemplateService } from '../../../services/documents/doc-template.service';
import { NotyService } from '../../../services/common/noty.service';
import { TinymceService } from '../../../services/common/tinymce.service';
import { UserService } from '../../../services/users/user.service';

import { findOpenTag, findEndOfBlock } from './tag-range';

declare var $: any;
declare var swal: any;
declare var window: any;

@Component({
  selector: 'app-doc-template-form',
  templateUrl: './doc-template-form.component.html',
  styleUrls: ['./doc-template-form.component.scss'],
  providers: [TinymceService],
})
export class DocTemplateFormComponent implements OnInit {
  /**
   * CSS files urls for template
   * @type {Array<any>}
   */
  css_files: Array<any> = [];

  /**
   * Errors for each doc part, blank if no error
   * @type {Array<string>}
   */
  docPartErrors: Array<string | undefined> = [];

  /**
   * Doc template object
   */
  docTemplate: DocTemplate = new DocTemplate();

  /**
   * Doc template id from url
   * @type {number}
   */
  docTemplateId: number;

  /**
   * Ng model variable for doc template name search
   * @type {string}
   */
  docTemplateSearchTerm: string;

  /**
   * Form options selects
   * @type {any}
   */
  formOptions: any = {
    activities: [],
    doc_part_types: [],
    logos: [],
    matter_types: [],
  };

  /**
   * Indicates if page is loading
   * @type {boolean}
   */
  loading: boolean = false;

  /**
   * Sortable options of Doc Templates sorting using Angular-sortablejs
   * @type {any}
   */
  sortableOptions: any = {
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    ghostClass: 'sortable-ghost',
    handle: '.alias-sort-handle',
    onEnd: (event: any) => {},
    onStart: (event: any) => {
      this.docTemplate.doc_parts[event.oldIndex].expanded = false;
    },
    onUpdate: (event: any) => {
      this.updateDocPartsPosition();
    },
  };

  /**
   * Stylesheet formats built using tinymce schema and stylesheets from the template
   * @type {any}
   */
  stylesheet_formats: any;

  /**
   * Check payee contact search component
   * @param {[type]} 'checkPayeeSearch' variable name
   */
  @ViewChild('checkPayeeSearch', { static: true }) checkPayeeSearchComponent: ContactSearchComponent;

  /**
   * Tinymce component for template description
   * @param {[type]} 'tinymce' variable name
   */
  @ViewChild('tinymce', { static: true }) tinymceDescComponent: TinymceComponent;

  /**
   * Tinymce components from doc part contents
   * @param {[type]} 'tinymceCss' variable name
   */
  @ViewChildren('tinymceCss') tinymceCssComponents: QueryList<TinymceCssComponent>;

  /**
   * Constructor
   * @param {ActivatedRoute}     private activatedRoute     Angular route with current route info
   * @param {DocPartService}     private docPartService     Service to access doc parts controller
   * @param {DocTemplateService} private docTemplateService Service to access doc templates controller
   * @param {Location}           private location           Angular service to modify url location
   * @param {NotyService}        private notyService        Service to show notifications in web UI
   * @param {Router}             private router             Angular router object to change page, create subscriptions for route params
   * @param {TinymceService}     private tinymceService     Service to build stylesheet formats for tinymce formats
   * @param {UserService}        private userService        Service to access users controller
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private docPartService: DocPartService,
    private docTemplateService: DocTemplateService,
    private location: Location,
    private notyService: NotyService,
    private router: Router,
    private tinymceService: TinymceService,
    private userService: UserService
  ) {}

  // Component lifecycle functions

  /**
   * Called on component initialization
   */
  ngOnInit() {
    this.loading = true;
    this.docTemplateService.formOptions().then((response_json) => {
      if (response_json) this.formOptions = response_json;
      this.userService.navbarComponent.setDocumentTemplatesMenu();
      if (this.activatedRoute && this.activatedRoute.snapshot && this.activatedRoute.snapshot.params && this.activatedRoute.snapshot.params.id) this.editDocTemplate(this.activatedRoute.snapshot.params.id);
      else this.newDocTemplate();
    });
  }

  /**
   * Add new doc part
   * @param {number} index Position to add doc part (adds after position)
   */
  addNewDocPart(index: number) {
    if (this.docTemplate && this.docTemplate.doc_parts) this.docTemplate.doc_parts.splice(index + 1, 0, new DocPart());
    console.log('Doc parts');
    console.log(this.docTemplate);
  }

  /**
   * Add stylesheet to template, does not save to server
   * @param {any} stylesheetToAdd Stylesheet to add
   */
  addStylesheet(stylesheetToAdd: any) {
    for (let stylesheet of this.docTemplate.stylesheets) if (stylesheetToAdd.value == stylesheet.value) return;
    this.docTemplate.stylesheets.push(stylesheetToAdd);
  }

  /**
   * Apply current list of stylesheets to tinymce editors
   */
  applyStylesheets() {
    this.css_files = [];
    this.stylesheet_formats = undefined;
    this.tinymceService.loadCSS(this.docTemplate.stylesheets, this, [], () => {
      console.log(this.docTemplate, this.css_files, this.stylesheet_formats);
      if (this.stylesheet_formats) {
        let scope: DocTemplateFormComponent = this;
        let expandedDocParts: Array<boolean> = [];
        for (let docPart of this.docTemplate.doc_parts) {
          expandedDocParts.push(docPart.expanded);
          docPart.expanded = false;
        }
        setTimeout(() => {
          for (let tinymceCssComponent of this.tinymceCssComponents.toArray()) tinymceCssComponent.initializeTinymce(scope.css_files, scope.stylesheet_formats);
          let index: number = 0;
          for (let docPart of this.docTemplate.doc_parts) {
            docPart.expanded = expandedDocParts[index];
            index += 1;
          }
        }, 1000);
      }
    });
  }

  /**
   * Delete doc part
   * @param {DocPart} docPart Doc part to delete
   * @param {number}  index   Position of doc part
   */
  deleteDocPart(docPart: DocPart, index: number) {
    swal({
      title: `Delete part`,
      text: `Delete part ${docPart.name} ?`,
      icon: 'warning',
      dangerMode: true,
      buttons: {
        cancel: {
          text: 'No',
          value: false,
          visible: true,
          className: '',
          closeModal: true,
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: '',
          closeModal: true,
        },
      },
    })
      .then((confirmed: any) => {
        if (confirmed) {
          docPart.deleting = true;
          this.docPartService
            .delete(docPart.id)
            .then((response) => {
              docPart.deleting = false;
              if (response.success) {
                this.notyService.notify(response.text || `Part deleted - ${docPart.id}`, 'warning');
                this.removeDocPart(index);
              } else {
                this.notyService.notify(response.text || 'Error deleting part', 'error');
              }
            })
            .catch(() => {
              docPart.deleting = false;
              this.notyService.notify('Error deleting part', 'error');
            });
        }
      })
      .catch((err: any) => {
        console.log('Delete prompt error', err);
      });
  }

  /**
   * Delete doc template
   */
  /**
   * Controls visibility of the inline clone-name input next to the Clone button.
   */
  showCloneInput: boolean = false;

  /**
   * Name of the template about to be cloned. Bound via ngModel to the input
   * that appears when the Clone button is clicked.
   */
  cloneName: string = '';

  /**
   * Indicates a clone request is in flight — disables the button to prevent
   * double-submit.
   */
  cloning: boolean = false;

  /**
   * Deep-clone the current template (copies the template row + every DocPart)
   * and navigate to the clone's edit page on success.
   */
  cloneDocTemplate() {
    if (!this.docTemplate || !this.docTemplate.id) {
      this.notyService.notify('Save the template before cloning it.', 'warning');
      return;
    }
    const name = (this.cloneName || '').trim();
    if (!name) {
      this.notyService.notify('Please enter a name for the cloned template.', 'warning');
      return;
    }
    this.cloning = true;
    this.docTemplateService
      .clone(this.docTemplate.id, name)
      .then((response) => {
        this.cloning = false;
        if (response && response.success && response.doc_template_id) {
          this.notyService.notify(response.text || 'Template cloned.', 'success');
          this.showCloneInput = false;
          this.cloneName = '';
          this.router.navigateByUrl(`/doc_templates/${response.doc_template_id}/edit`).then(() => {
            // Force a full component reload so all bound state reflects the clone.
            window.location.reload();
          });
        } else {
          this.notyService.notify((response && response.text) || 'Clone failed.', 'error');
        }
      })
      .catch((err: any) => {
        this.cloning = false;
        const msg = (err && err.error && err.error.text) || 'Clone request failed.';
        this.notyService.notify(msg, 'error');
      });
  }

  deleteDocTemplate() {
    swal({
      title: `Delete template`,
      text: `Delete template ${this.docTemplate.name} ?`,
      icon: 'warning',
      dangerMode: true,
      buttons: {
        cancel: {
          text: 'No',
          value: false,
          visible: true,
          className: '',
          closeModal: true,
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: '',
          closeModal: true,
        },
      },
    })
      .then((confirmed: any) => {
        if (confirmed) {
          this.docTemplate.deleting = true;
          this.docTemplateService
            .delete(this.docTemplate.id)
            .then((response) => {
              this.docTemplate.deleting = false;
              if (response.success) {
                this.notyService.notify(response.text || `Template deleted - ${this.docTemplate.id}`, 'warning');
                this.router.navigateByUrl('/doc_templates');
              } else {
                this.notyService.notify(response.text || 'Error deleting template', 'error');
              }
            })
            .catch(() => {
              this.docTemplate.deleting = false;
              this.notyService.notify('Error deleting template', 'error');
            });
        }
      })
      .catch((err: any) => {
        console.log('Delete prompt error', err);
      });
  }

  /**
   * Used to switch doc part to selected doc part from autocomplete
   * @param {number}              id                  Id of doc part to load
   * @param {number}              index               Position of doc part to replace
   * @param {TinymceCssComponent} tinymceCssComponent tinymce component of doc part contents field
   */
  editDocPart(id: number, index: number, tinymceCssComponent: TinymceCssComponent | null) {
    let docPart: any = this.docTemplate.doc_parts[index];
    docPart.loading = true;
    this.docPartService
      .edit(id)
      .then((response_json) => {
        docPart.loading = false;
        if (response_json.doc_part) {
          for (let [key, value] of window.Object.entries(response_json.doc_part)) docPart[key] = value;
          docPart.searchTerm = docPart.name;
          if(!tinymceCssComponent) {
            tinymceCssComponent = this.tinymceCssComponents.toArray()[index];
          }
          if (tinymceCssComponent) tinymceCssComponent.setContent(docPart.contents);
          this.saveDocTemplate();
        }
      })
      .catch(() => {
        docPart.loading = false;
      });
  }

  /**
   * Switch doc template to selection from autocomplete
   * @param {number} id Id of doc template to load
   */
  editDocTemplate(id: number) {
    this.loading = true;
    this.docTemplateService
      .edit(id)
      .then((response_json) => {
        this.loading = false;
        if (response_json && response_json.doc_template) {
          if (this.docTemplate && this.docTemplate.id != response_json.doc_template.id) this.location.go(`/doc_templates/${id}/edit`);
          this.docTemplate = response_json.doc_template;
          this.docTemplateId = id;
          this.docTemplateSearchTerm = this.docTemplate.name;
          if (this.docTemplate && this.docTemplate.doc_parts) for (let docPart of this.docTemplate.doc_parts) docPart.searchTerm = docPart.name;
          this.applyStylesheets();
          if (this.docTemplate.doc_parts && this.docTemplate.doc_parts.length < 1) this.addNewDocPart(0);
        } else this.notyService.notify('Error loading template', 'error');
      })
      .catch(() => {
        this.loading = false;
      });
  }

  /**
   * Prepare a new template
   */
  newDocTemplate() {
    this.loading = true;
    this.docTemplateService
      .newTemplate()
      .then((response_json) => {
        this.loading = false;
        if (response_json && response_json.doc_template) {
          this.docTemplate = response_json.doc_template;
          if (this.docTemplate && this.docTemplate.doc_parts) for (let docPart of this.docTemplate.doc_parts) docPart.searchTerm = docPart.name;
          this.docTemplateSearchTerm = this.docTemplate.name;
          this.applyStylesheets();
          this.location.go('/doc_templates/new');
        } else this.notyService.notify('Error loading new template page', 'error');
      })
      .catch(() => {
        this.loading = false;
      });
  }

  /**
   * Preview doc template
   * @param {boolean = false} showInIframe Indicates if preview should show in iframe
   * @param {boolean = false} htmlDebug    Indicates if preview response should be in HTML
   */
  previewDocTemplate(showInIframe: boolean = false, htmlDebug: boolean = false) {
    let inputs: string = '';
    inputs += `<input type="hidden" name="logo_id" value="${this.docTemplate.logo_id}" />`;
    inputs += `<input type="hidden" name="matter_id" value="${this.docTemplate.matter_id}" />`;
    inputs += `<input type="hidden" name="merge_parts" value="${this.docTemplate.merge_parts}" />`;
    inputs += `<input type="hidden" name="watermark_start_page" value="${this.docTemplate.watermark_start_page}" />`;
    inputs += '<input type="hidden" name="initial_run" value="true" />';
    for (let stylesheet of this.docTemplate.stylesheets) inputs += `<input type="hidden" name="stylesheet_ids[]" value="${stylesheet.id}" />`;
    for (let docPart of this.docTemplate.doc_parts) {
      inputs += `<input type="hidden" name="doc_parts_name[]" value="${docPart.name}" />`;
      inputs += `<input type="hidden" name="doc_parts_type[]" value="${docPart.type}" />`;
      inputs += `<textarea name="doc_parts_content[]">${(docPart.contents || '').replace(/'/g, "\\'")}</textarea>`;
    }
    if (htmlDebug) inputs += '<input type="hidden" name="html_debug" value="true" />';
    // TODO: check if debugging is still available for preview, remove next two lines if not so
    //inputs             += '<input type="hidden" name="debug" value="'    + ($scope.debug == true) + '" />';
    //inputs             += '<input type="hidden" name="debug_js" value="' + ($scope.debug_js == true) + '" />';
    if (showInIframe) {
      $('#preview_inline_holder').css('height', '30cm');
      inputs += '<input type="hidden" name="disposition" value="inline" />';
      $(`<form target="preview_content" action="/doc_templates/preview.pdf" method="post">${inputs}</form>`).appendTo('body').submit().remove();
      $('html, body').animate({ scrollTop: $('#preview_content').offset().top }, 500);
    } else {
      inputs += '<input type="hidden" name="disposition" value="attachment" />';
      $(`<form action="/doc_templates/preview.pdf" method="post">${inputs}</form>`).appendTo('body').submit().remove();
    }
  }

  /**
   * Remove doc part
   * @param {number} index Position of doc part to remove
   */
  removeDocPart(index: number) {
    if (this.docTemplate && this.docTemplate.doc_parts) {
      if (this.docTemplate.doc_parts.length < 2) {
        this.notyService.notify('Atleast one part must be present for a template', 'warning', 5000, 'center');
        return;
      }
      let removedDocPart: DocPart = this.docTemplate.doc_parts.splice(index, 1)[0];
      if (removedDocPart.id) this.saveDocTemplate();
    }
  }

  /**
   * Remove stylesheet
   * @param {number} index Position of stylesheet to remove
   */
  removeStylesheet(index: number) {
    this.docTemplate.stylesheets.splice(index, 1);
    this.applyStylesheets();
  }

  /**
   * Save doc part
   * @param {DocPart} docPart Doc part to save
   * @param {number}  index   Position of doc part to save
   */
  /**
   * Detach the given part from the DocPart row it currently points at, give
   * it a new name, and save — which creates a brand-new DocPart. After the
   * new part is saved, the containing DocTemplate is re-saved so this
   * template's DocTemplatePart now links to the new id.
   *
   * Use this when a part is shared with other templates and the user wants
   * their edits to apply ONLY to this template.
   */
  saveDocPartAsNew(docPart: DocPart, index: number) {
    if (!docPart) return;
    // Flush TinyMCE contents into docPart.contents first.
    for (const tc of this.tinymceCssComponents.toArray()) tc.triggerContentOutput();
    const base = ((docPart.searchTerm || docPart.name || 'Part') + '').trim();

    swal({
      title: 'Save as New Part',
      text: 'Enter a name for the new part. The existing part is shared with any other templates that use it — the new part will be independent.',
      content: 'input',
      buttons: ['Cancel', 'Create'],
    }).then((entered: any) => {
      if (entered === null || entered === false) return;
      const newName = (((entered as string) || '') + '').trim() || `${base} (copy)`;
      // Detaching: clear the id so the save endpoint creates a new DocPart row.
      docPart.id         = null as any;
      docPart.searchTerm = newName;
      docPart.name       = newName;
      this.saveDocPart(docPart, index);
    });
  }

  /**
   * Static tag validator result for each part. Indexed by part index so
   * each part can render its own error panel.
   */
  tagErrors: { [index: number]: any[] } = {};

  /**
   * Aggregated results when the user clicks "Check All Tags" at the
   * template level. Each entry corresponds to one part.
   */
  allTagResults: { index: number; name: string; valid: boolean; errors: any[] }[] | null = null;

  /**
   * True while a Check All Tags run is in flight.
   */
  checkingAllTags: boolean = false;

  /**
   * Run the tag validator over every part on the current template and
   * aggregate results. Shown in a collapsible panel at the top of the
   * form so authors can see the whole picture at once.
   */
  checkAllTags() {
    if (!this.docTemplate || !this.docTemplate.doc_parts) return;
    for (const tc of this.tinymceCssComponents.toArray()) tc.triggerContentOutput();
    this.checkingAllTags = true;
    this.allTagResults = [];
    const parts = this.docTemplate.doc_parts;
    const promises: Promise<any>[] = parts.map((dp: DocPart, idx: number) =>
      this.docPartService
        .validateTags((dp.contents || '') + '')
        .then((response: any) => ({
          index: idx,
          name: dp.name || `Part ${idx + 1}`,
          valid: !!(response && response.valid),
          errors: (response && response.errors) || [],
        }))
        .catch(() => ({
          index: idx,
          name: dp.name || `Part ${idx + 1}`,
          valid: false,
          errors: [{ severity: 'error', message: 'Tag check failed for this part.' }],
        }))
    );
    Promise.all(promises).then((results) => {
      this.checkingAllTags = false;
      this.allTagResults = results.sort((a, b) => a.index - b.index);
    });
  }

  /** Dismiss the Check All Tags panel. */
  clearAllTagResults() {
    this.allTagResults = null;
  }

  /**
   * Jump to an error's snippet in the given part's TinyMCE editor:
   * expand the part, look the editor up by its elementId (more reliable
   * than walking ViewChildren, which skips to/cc/signature parts that
   * don't render an <app-tinymce-css>), find the snippet text in the
   * editor body, select it, and scroll it into view.
   */
  jumpToSnippet(partIndex: number, snippet: string): boolean {
    if (!snippet) return false;
    if (this.docTemplate && this.docTemplate.doc_parts && this.docTemplate.doc_parts[partIndex]) {
      this.docTemplate.doc_parts[partIndex].expanded = true;
    }

    const attempt = (tries: number) => {
      const tmce: any = (window as any).tinymce;
      // Mirrors the elementId pattern from doc-template-form.component.html:
      // <app-tinymce-css [elementId]="'tinymce-' + i + '-doc-part-' + i">.
      const elementId = 'tinymce-' + partIndex + '-doc-part-' + partIndex;
      const editor: any = tmce && tmce.get && tmce.get(elementId);
      if (!editor) {
        if (tries > 0) {
          window.setTimeout(() => attempt(tries - 1), 200);
        } else {
          this.notyService.notify(
            'Could not find the TinyMCE editor for this part. Expand it manually and try again.',
            'warning'
          );
        }
        return;
      }

      const body = editor.getBody();
      if (!body) return;

      // First pass: text nodes — catches the common case where the tag is a
      // single plain-text run.
      const doc = body.ownerDocument;
      const walker = doc.createTreeWalker(body, 4 /* NodeFilter.SHOW_TEXT */);
      let node: any;
      while ((node = walker.nextNode())) {
        const idx = (node.nodeValue || '').indexOf(snippet);
        if (idx >= 0) {
          const range = editor.dom.createRng();
          range.setStart(node, idx);
          range.setEnd(node, idx + snippet.length);
          editor.selection.setRng(range);
          editor.selection.scrollIntoView();
          editor.focus();
          return;
        }
      }

      // Second pass: flat textContent search — handles tags split across
      // formatting runs (e.g. "<" in plain text, "r:if_type>" inside a bold
      // span). We find the index in the flat string and map it back to the
      // starting text node at that offset.
      const flat: string = body.textContent || '';
      const flatIdx = flat.indexOf(snippet);
      if (flatIdx >= 0) {
        let cursor = 0;
        const walker2 = doc.createTreeWalker(body, 4);
        while ((node = walker2.nextNode())) {
          const len = (node.nodeValue || '').length;
          if (cursor + len > flatIdx) {
            const range = editor.dom.createRng();
            const startOffset = flatIdx - cursor;
            range.setStart(node, Math.min(startOffset, len));
            range.setEnd(node, Math.min(startOffset, len));
            editor.selection.setRng(range);
            editor.selection.scrollIntoView();
            editor.focus();
            this.notyService.notify(
              'Landed near the tag. Formatting split the text — use Ctrl-F for a precise match.',
              'info'
            );
            return;
          }
          cursor += len;
        }
      }

      this.notyService.notify(
        'Snippet not found in the editor. Try searching (Ctrl-F) for: ' + snippet,
        'warning'
      );
    };
    attempt(8);
    return true;
  }

  /** Best-effort clipboard copy of a snippet. */
  copySnippet(snippet: string) {
    try {
      if (navigator && (navigator as any).clipboard) {
        (navigator as any).clipboard.writeText(snippet).then(() => {
          this.notyService.notify('Snippet copied', 'success');
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = snippet;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        this.notyService.notify('Snippet copied', 'success');
      }
    } catch {
      this.notyService.notify('Copy failed — select and copy manually.', 'error');
    }
  }

  /**
   * Run the TagValidator over the current part's contents and populate
   * tagErrors[index] with the result. Called by the "Check Tags" button
   * on each part.
   */
  checkTags(docPart: DocPart, index: number) {
    // Flush TinyMCE contents first so we check what the user just typed.
    for (const tc of this.tinymceCssComponents.toArray()) tc.triggerContentOutput();
    this.tagErrors[index] = [{ severity: 'pending', message: 'Checking…' }];
    this.docPartService
      .validateTags((docPart.contents || '') + '')
      .then((response: any) => {
        if (response && response.valid) {
          this.tagErrors[index] = [{ severity: 'ok', message: 'No tag errors found.' }];
        } else {
          this.tagErrors[index] = response.errors || [];
        }
      })
      .catch((err: any) => {
        this.tagErrors[index] = [
          { severity: 'error', message: 'Tag check failed: ' + (err?.error?.text || err?.message || 'Unknown error') },
        ];
      });
  }

  /**
   * Dismiss the tag-error panel for a specific part.
   */
  clearTagErrors(index: number) {
    delete this.tagErrors[index];
  }

  /**
   * Extend the TinyMCE selection in the given part from the Radius tag under
   * the cursor (or the tag the user selected) through its matching </r:name>.
   * Handles nesting of like-named tags and self-closing tags.
   *
   * Everything works off body.textContent, which is the concatenation of text
   * nodes: Radius tags live inside those nodes as literal `<…>` characters
   * (TinyMCE only encodes them as entities when serializing HTML), so offsets
   * into textContent map cleanly back to (node, offset) via a text-node walk.
   */
  selectToEndTag(partIndex: number): void {
    if (this.docTemplate && this.docTemplate.doc_parts && this.docTemplate.doc_parts[partIndex]) {
      this.docTemplate.doc_parts[partIndex].expanded = true;
    }
    const tmce: any = (window as any).tinymce;
    const elementId = 'tinymce-' + partIndex + '-doc-part-' + partIndex;
    const editor: any = tmce && tmce.get && tmce.get(elementId);
    if (!editor) {
      this.notyService.notify('Editor not found for this part. Expand it and try again.', 'warning');
      return;
    }
    const body = editor.getBody();
    if (!body) return;

    const flat: string = body.textContent || '';
    const sel = editor.selection;
    const selectedText: string = (() => {
      try {
        return (sel.getRng().cloneContents().textContent || '') as string;
      } catch {
        return '';
      }
    })();
    const caret = this.getCaretFlatOffset(editor, body);

    const open = findOpenTag(flat, caret, selectedText);
    if (!open) {
      this.notyService.notify('Place the cursor inside a <r:…> tag first, or select one.', 'info');
      return;
    }
    if (open.selfClose) {
      this.selectFlatRange(editor, body, open.start, open.end);
      this.notyService.notify('Self-closing tag — selected the tag itself.', 'info');
      return;
    }
    const end = findEndOfBlock(flat, open);
    if (end < 0) {
      this.notyService.notify(`No matching </r:${open.name}> found — the tag may be unclosed.`, 'warning');
      return;
    }
    this.selectFlatRange(editor, body, open.start, end);
  }

  private getCaretFlatOffset(editor: any, body: any): number {
    try {
      const rng = editor.selection.getRng();
      const probe = editor.dom.createRng();
      probe.setStart(body, 0);
      probe.setEnd(rng.startContainer, rng.startOffset);
      return (probe.cloneContents().textContent || '').length;
    } catch {
      return 0;
    }
  }

  private selectFlatRange(editor: any, body: any, startFlat: number, endFlat: number): void {
    const doc = body.ownerDocument;
    const walker = doc.createTreeWalker(body, 4 /* NodeFilter.SHOW_TEXT */);
    let node: any;
    let cursor = 0;
    let startNode: any = null;
    let startOffset = 0;
    let endNode: any = null;
    let endOffset = 0;
    while ((node = walker.nextNode())) {
      const len = (node.nodeValue || '').length;
      if (!startNode && cursor + len >= startFlat) {
        startNode = node;
        startOffset = Math.max(0, Math.min(len, startFlat - cursor));
      }
      if (!endNode && cursor + len >= endFlat) {
        endNode = node;
        endOffset = Math.max(0, Math.min(len, endFlat - cursor));
        break;
      }
      cursor += len;
    }
    if (!startNode || !endNode) {
      this.notyService.notify('Could not map the selection back to the editor DOM.', 'warning');
      return;
    }
    const range = editor.dom.createRng();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    editor.selection.setRng(range);
    editor.selection.scrollIntoView();
    editor.focus();
  }

  saveDocPart(docPart: DocPart, index: number) {
    docPart.saving = true;
    docPart.name = docPart.searchTerm;
    for (let tinymceCssComponent of this.tinymceCssComponents.toArray()) tinymceCssComponent.triggerContentOutput();
    let scope: DocTemplateFormComponent = this;
    window.setTimeout(() => {
      scope.docPartService
        .save(docPart)
        .then((response_json) => {
          docPart.saving = false;
          if (response_json.success) {
            let isNewDocPart: boolean = !docPart.id;
            scope.notyService.notify(response_json.text || 'Part saved', 'success');
            if (!docPart.id && response_json.id) docPart.id = response_json.id;
            if (isNewDocPart) scope.saveDocTemplate();
          } else scope.notyService.notify(response_json.text || 'Error saving part', 'error');
          if (scope.docPartErrors && scope.docPartErrors[index]) scope.docPartErrors[index] = undefined;
        })
        .catch(() => {
          docPart.saving = false;
          scope.notyService.notify('Error saving part', 'error');
        });
    }, 500);
  }

  /**
   * Save doc template
   */
  saveDocTemplate() {
    this.docTemplate.saving = true;
    let oldName: string = this.docTemplate.name;
    if (this.docTemplate && this.docTemplate.doc_parts) for (let docPart of this.docTemplate.doc_parts) docPart.name = docPart.searchTerm;
    this.docTemplate.name = this.docTemplateSearchTerm;
    for (let tinymceCssComponent of this.tinymceCssComponents.toArray()) tinymceCssComponent.triggerContentOutput();
    if (this.tinymceDescComponent) this.tinymceDescComponent.triggerContentOutput();
    let scope: DocTemplateFormComponent = this;
    window.setTimeout(() => {
      scope.docTemplateService
        .save(this.docTemplate)
        .then((response_json) => {
          scope.docTemplate.saving = false;
          if (response_json.success) {
            scope.notyService.notify(response_json.text || 'Template saved', 'success');
            if (!scope.docTemplate.id && response_json.doc_template_id) scope.location.go(`/doc_templates/${response_json.doc_template_id}/edit`);
            if (response_json.doc_template_id) scope.docTemplate.id = response_json.doc_template_id;
            if (response_json.doc_part_ids) {
              let index: number = 0;
              for (let docPart of scope.docTemplate.doc_parts) {
                if (response_json.doc_part_ids[index]) docPart.id = response_json.doc_part_ids[index];
                index += 1;
              }
            }
            scope.docPartErrors = [];
          } else {
            scope.notyService.notify(response_json.text || 'Error saving template', 'error');
            scope.docTemplate.name = oldName;
            if (response_json.doc_part_errors) scope.docPartErrors = response_json.doc_part_errors;
          }
        })
        .catch(() => {
          scope.docTemplate.saving = false;
          scope.docTemplate.name = oldName;
          scope.docPartErrors = [];
          scope.notyService.notify('Error saving template', 'error');
        });
    }, 500);
  }

  /**
   * Save stylesheets to doc template
   */
  saveStylesheets() {
    if (!this.docTemplate || !this.docTemplate.stylesheets) {
      this.notyService.notify('Error in page, please refresh and try again', 'error');
      return;
    }
    this.docTemplate.saving = true;
    let stylesheetIds: Array<number> = [];
    for (let stylesheet of this.docTemplate.stylesheets) stylesheetIds.push(stylesheet.id);
    this.docTemplateService
      .saveStylesheets(this.docTemplate.id, stylesheetIds)
      .then((response_json) => {
        this.docTemplate.saving = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Stylesheets saved', 'success');
          this.applyStylesheets();
        } else this.notyService.notify(response_json.text || 'Error saving stylesheets', 'error');
      })
      .catch(() => {
        this.notyService.notify('Error saving stylesheets', 'error');
        this.docTemplate.saving = false;
      });
  }

  /**
   * Update doc parts position on sorting of doc parts
   */
  updateDocPartsPosition() {
    this.saveDocTemplate();
  }
}
